"use client"

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react"
import {
  loadHCaptchaRuntime,
  type HCaptchaApi,
  type HCaptchaWidgetId,
} from "./runtime"

export type HCaptchaWidgetHandle = {
  reset(): void
}

type HCaptchaWidgetProps = {
  siteKey: string
  onToken(token: string): void
  onExpired(): void
  onError(): void
}

const HCaptchaWidget = forwardRef<HCaptchaWidgetHandle, HCaptchaWidgetProps>(
  function HCaptchaWidget({ siteKey, onToken, onExpired, onError }, ref) {
    const hostRef = useRef<HTMLDivElement>(null)
    const apiRef = useRef<HCaptchaApi | null>(null)
    const widgetId = useRef<HCaptchaWidgetId | null>(null)

    useImperativeHandle(ref, () => ({
      reset() {
        if (widgetId.current !== null) {
          apiRef.current?.reset(widgetId.current)
        }
      },
    }), [])

    useEffect(() => {
      let cancelled = false
      if (!siteKey) return

      loadHCaptchaRuntime()
        .then((api) => {
          if (cancelled || !hostRef.current) return
          apiRef.current = api
          widgetId.current = api.render(hostRef.current, {
            sitekey: siteKey,
            hl: "tr",
            callback: (token: string) => onToken(token),
            "expired-callback": onExpired,
            "error-callback": onError,
          })
        })
        .catch(onError)

      return () => {
        cancelled = true
        if (widgetId.current !== null && apiRef.current?.remove) {
          apiRef.current.remove(widgetId.current)
        }
        widgetId.current = null
        apiRef.current = null
      }
    }, [siteKey, onError, onExpired, onToken])

    return <div ref={hostRef} aria-label="hCaptcha doğrulama alanı" />
  }
)

export default HCaptchaWidget
